import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/Card";
import { Btn } from "@/components/Btn";
import { EurInput } from "@/components/EurInput";
import { SectionHeader } from "@/components/SectionHeader";
import { SettingsRow, S_INPUT } from "@/components/SettingsRow";
import { ApiError } from "@/api/client";
import {
  PriceRow,
  SettingsConfig,
  SettingsPrices,
  getSettingsConfig,
  getSettingsPrices,
  putSettingsConfig,
  putSettingsPrices,
} from "@/api/settings";
import { SAISONS } from "@/api/constants";

type Props = { isAdmin: boolean };

const DEFAULT_GASTTYPEN = ["Standard", "Pfadfinder"];
const DEFAULT_SAISONS = SAISONS;

function priceKey(einheitId: number, gastTyp: string, saison: string) {
  return `${einheitId}|${gastTyp}|${saison}`;
}

function buildEditState(data: SettingsPrices): Record<string, PriceRow> {
  const out: Record<string, PriceRow> = {};
  const gasttypen = data.gasttypen.length ? data.gasttypen : DEFAULT_GASTTYPEN;
  const saisons = data.saisons.length ? data.saisons : DEFAULT_SAISONS;
  const seen = new Set<string>();
  for (const r of data.rows) {
    const k = priceKey(r.einheit_id, r.gast_typ, r.saison);
    out[k] = { ...r };
    seen.add(k);
  }
  for (const e of data.einheiten) {
    for (const gt of gasttypen) {
      for (const sa of saisons) {
        const k = priceKey(e.id, gt, sa);
        if (!seen.has(k)) {
          out[k] = {
            einheit_id: e.id,
            gast_typ: gt,
            saison: sa,
            saison_von: sa === "Winter" ? "2025-10-01" : "2025-04-01",
            saison_bis: sa === "Winter" ? "2030-03-31" : "2030-09-30",
            preis_pro_nacht: 0,
          };
        }
      }
    }
  }
  return out;
}

export function PreiseSection({ isAdmin }: Props) {
  const qc = useQueryClient();
  const pricesQ = useQuery({ queryKey: ["settings-prices"], queryFn: getSettingsPrices });
  const configQ = useQuery({ queryKey: ["settings-config"], queryFn: getSettingsConfig });

  const [edit, setEdit] = useState<Record<string, PriceRow>>({});
  const [pricesDirty, setPricesDirty] = useState(false);
  const [pricesMsg, setPricesMsg] = useState<string | null>(null);

  const [cfg, setCfg] = useState<SettingsConfig>({
    KURTAXE_AB_16: "",
    KURTAXE_6_BIS_15: "",
    KURTAXE_UNTER_6: "",
    ANZAHLUNG_BETRAG: "",
    STORNO_SCHWELLE_TAGE: "",
    STORNO_PCT: "",
    DASHBOARD_KONUS_LINK_FRIST_TAGE: "",
    DASHBOARD_KONUS_DATEN_FRIST_TAGE: "",
    DASHBOARD_INFOS_GRUPPE_FRIST_TAGE: "",
    DASHBOARD_RECHNUNG_EINGANG_FRIST_TAGE: "",
    DASHBOARD_ANZAHLUNG_FRIST_TAGE: "",
    FORMULARE_ANFRAGE_AKTIV: "1",
    FORMULARE_KONUS_AKTIV: "1",
    KONUS_HOTELID: "",
    KONUS_KATEGORIE_ERWACHSEN: "",
    KONUS_KATEGORIE_KIND: "",
    KONUS_KATEGORIE_KLEINKIND: "",
    CI_NAME: "",
    CI_PRIMAERFARBE: "",
    CI_AKZENTFARBE: "",
    CI_ADRESSE: "",
    CI_TELEFON: "",
    CI_EMAIL: "",
    CI_WEBSITE: "",
    CI_FONT_UEBERSCHRIFT: "",
    CI_FONT_TEXT: "",
  });
  const [cfgDirty, setCfgDirty] = useState(false);
  const [cfgMsg, setCfgMsg] = useState<string | null>(null);

  useEffect(() => {
    if (pricesQ.data) {
      setEdit(buildEditState(pricesQ.data));
      setPricesDirty(false);
    }
  }, [pricesQ.data]);

  useEffect(() => {
    if (configQ.data) {
      setCfg(configQ.data);
      setCfgDirty(false);
    }
  }, [configQ.data]);

  const einheiten = pricesQ.data?.einheiten ?? [];
  const gasttypen = useMemo(
    () => (pricesQ.data?.gasttypen.length ? pricesQ.data!.gasttypen : DEFAULT_GASTTYPEN),
    [pricesQ.data],
  );
  const saisons = useMemo(
    () => (pricesQ.data?.saisons.length ? pricesQ.data!.saisons : DEFAULT_SAISONS),
    [pricesQ.data],
  );

  const updatePrice = (k: string, val: number) => {
    setEdit((prev) => ({ ...prev, [k]: { ...prev[k], preis_pro_nacht: val } }));
    setPricesDirty(true);
  };

  const savePricesMut = useMutation({
    mutationFn: () => putSettingsPrices(Object.values(edit)),
    onSuccess: async () => {
      setPricesMsg("Preise gespeichert.");
      setPricesDirty(false);
      await qc.invalidateQueries({ queryKey: ["settings-prices"] });
    },
    onError: (e: unknown) => {
      setPricesMsg(e instanceof ApiError ? e.message : "Speichern fehlgeschlagen.");
    },
  });

  const saveCfgMut = useMutation({
    mutationFn: () => putSettingsConfig(cfg),
    onSuccess: async (next) => {
      setCfg(next);
      setCfgDirty(false);
      setCfgMsg("Gespeichert.");
      await qc.invalidateQueries({ queryKey: ["settings-config"] });
    },
    onError: (e: unknown) => {
      setCfgMsg(e instanceof ApiError ? e.message : "Speichern fehlgeschlagen.");
    },
  });

  return (
    <>
      {!isAdmin && (
        <Card style={{ marginBottom: 16, background: "var(--bg-subtle)" }}>
          <div style={{ fontSize: 12, color: "var(--fg-muted)" }}>
            <span className="material-icons-outlined" style={{ fontSize: 16, verticalAlign: "-3px" }}>
              info
            </span>{" "}
            Nur lesbar — Änderungen können nur Administratoren speichern.
          </div>
        </Card>
      )}

      {gasttypen.map((gt) => (
        <Card key={gt} padded={false} style={{ marginBottom: 16 }}>
          <div style={{ padding: "var(--pad-card)", borderBottom: "1px solid var(--border)" }}>
            <SectionHeader
              title={`Preise · ${gt}`}
              desc="Preis pro Person und Nacht. Sommer = 1.4.–30.9., Winter = 1.10.–31.3."
            />
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "var(--bg-subtle)" }}>
                <th style={thStyle}>Einheit</th>
                {saisons.map((sa) => (
                  <th key={sa} style={thStyle}>
                    {sa}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {einheiten.map((e) => (
                <tr key={e.id} style={{ borderTop: "1px solid var(--border)" }}>
                  <td style={tdStyle}>
                    <span style={{ fontWeight: 600 }}>{e.name}</span>
                  </td>
                  {saisons.map((sa) => {
                    const k = priceKey(e.id, gt, sa);
                    const row = edit[k];
                    return (
                      <td key={sa} style={tdStyle}>
                        <EurInput
                          disabled={!isAdmin}
                          style={{ ...S_INPUT, maxWidth: 120 }}
                          value={row?.preis_pro_nacht ?? 0}
                          onChange={(val) => updatePrice(k, val)}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
              {einheiten.length === 0 && (
                <tr>
                  <td colSpan={saisons.length + 1} style={{ padding: 16, color: "var(--fg-muted)" }}>
                    Keine Einheiten konfiguriert.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Card>
      ))}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 16 }}>
        {pricesMsg && (
          <span style={{ fontSize: 12, color: "var(--fg-muted)", alignSelf: "center" }}>
            {pricesMsg}
          </span>
        )}
        <Btn
          variant="primary"
          icon="check"
          onClick={() => savePricesMut.mutate()}
          disabled={!isAdmin || !pricesDirty || savePricesMut.isPending}
        >
          Preise speichern
        </Btn>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <SectionHeader
          title="Kurtaxe"
          desc="Pro Person und Nacht. Altersgrenzen sind im Code festgelegt."
        />
        <SettingsRow label="Kurtaxe ab 16 Jahren">
          <EurInput
            style={{ ...S_INPUT, maxWidth: 140 }}
            disabled={!isAdmin}
            value={parseFloat(cfg.KURTAXE_AB_16) || 0}
            onChange={(val) => { setCfg({ ...cfg, KURTAXE_AB_16: String(val) }); setCfgDirty(true); }}
          />
        </SettingsRow>
        <SettingsRow label="Kurtaxe 6–15 Jahre">
          <EurInput
            style={{ ...S_INPUT, maxWidth: 140 }}
            disabled={!isAdmin}
            value={parseFloat(cfg.KURTAXE_6_BIS_15) || 0}
            onChange={(val) => { setCfg({ ...cfg, KURTAXE_6_BIS_15: String(val) }); setCfgDirty(true); }}
          />
        </SettingsRow>
        <SettingsRow label="Kurtaxe unter 6 Jahre" hint="Üblicherweise frei">
          <EurInput
            style={{ ...S_INPUT, maxWidth: 140 }}
            disabled={!isAdmin}
            value={parseFloat(cfg.KURTAXE_UNTER_6) || 0}
            onChange={(val) => { setCfg({ ...cfg, KURTAXE_UNTER_6: String(val) }); setCfgDirty(true); }}
          />
        </SettingsRow>
      </Card>

      <Card style={{ marginBottom: 16 }}>
        <SectionHeader
          title="Stornoregelung"
          desc="Wird ab dem Schwellentag und für jede spätere Stornierung verrechnet. Kurtaxe fällt nicht an."
        />
        <SettingsRow label="Schwelle (Tage vor Anreise)">
          <input
            style={{ ...S_INPUT, maxWidth: 140 }}
            type="number"
            step="1"
            min={0}
            disabled={!isAdmin}
            value={cfg.STORNO_SCHWELLE_TAGE}
            onChange={(e) => {
              setCfg({ ...cfg, STORNO_SCHWELLE_TAGE: e.target.value });
              setCfgDirty(true);
            }}
          />
        </SettingsRow>
        <SettingsRow label="Stornogebühr (% des Unterkunftspreises)">
          <input
            style={{ ...S_INPUT, maxWidth: 140 }}
            type="number"
            step="1"
            min={0}
            max={100}
            disabled={!isAdmin}
            value={cfg.STORNO_PCT}
            onChange={(e) => {
              setCfg({ ...cfg, STORNO_PCT: e.target.value });
              setCfgDirty(true);
            }}
          />
        </SettingsRow>
      </Card>

      <Card>
        <SectionHeader title="Anzahlung" desc="Standard-Anzahlungsbetrag bei neuen Buchungen." />
        <SettingsRow label="Anzahlungsbetrag (EUR)">
          <EurInput
            style={{ ...S_INPUT, maxWidth: 140 }}
            disabled={!isAdmin}
            value={parseFloat(cfg.ANZAHLUNG_BETRAG) || 0}
            onChange={(val) => { setCfg({ ...cfg, ANZAHLUNG_BETRAG: String(val) }); setCfgDirty(true); }}
          />
        </SettingsRow>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
          {cfgMsg && (
            <span style={{ fontSize: 12, color: "var(--fg-muted)", alignSelf: "center" }}>
              {cfgMsg}
            </span>
          )}
          <Btn
            variant="primary"
            icon="check"
            onClick={() => saveCfgMut.mutate()}
            disabled={!isAdmin || !cfgDirty || saveCfgMut.isPending}
          >
            Speichern
          </Btn>
        </div>
      </Card>
    </>
  );
}

const thStyle = {
  padding: "10px 14px",
  textAlign: "left" as const,
  fontSize: 11,
  textTransform: "uppercase" as const,
  letterSpacing: ".06em",
  color: "var(--fg-muted)",
  fontWeight: 600,
};

const tdStyle = { padding: "10px 14px" };
